import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DEMOGRAPHIC_LABELS } from "@/lib/copy";
import type {
  AtsiFilter,
  DemographicFilters,
  LboteFilter,
  SexFilter,
} from "@/lib/demographic-filters";
import { isDemographicFilterActive } from "@/lib/demographic-filters";

interface DemographicFilterBarProps {
  value: DemographicFilters;
  onChange: (next: DemographicFilters) => void;
  totalCount?: number;
  filteredCount?: number;
}

const sexOpts: ReadonlyArray<{ id: SexFilter; label: string }> = [
  { id: "all", label: DEMOGRAPHIC_LABELS.sex.all },
  { id: "F", label: DEMOGRAPHIC_LABELS.sex.F },
  { id: "M", label: DEMOGRAPHIC_LABELS.sex.M },
];

const atsiOpts: ReadonlyArray<{ id: AtsiFilter; label: string }> = [
  { id: "all", label: DEMOGRAPHIC_LABELS.atsi.all },
  { id: "yes", label: "ATSI" },
  { id: "no", label: DEMOGRAPHIC_LABELS.atsi.no },
];

const lboteOpts: ReadonlyArray<{ id: LboteFilter; label: string }> = [
  { id: "all", label: DEMOGRAPHIC_LABELS.lbote.all },
  { id: "lbote", label: "LBOTE" },
  { id: "non_lbote", label: "English" },
];

function Cluster<T extends string>({
  label,
  ariaLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  ariaLabel: string;
  value: T;
  options: ReadonlyArray<{ id: T; label: string }>;
  onChange: (next: T) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--fg-3)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
        {label}
      </span>
      <ToggleGroup
        type="single"
        size="sm"
        aria-label={ariaLabel}
        value={value}
        onValueChange={(v) => { if (v) onChange(v as T); }}
        style={{ background: "#fff", borderRadius: 7, padding: 2, border: "1px solid var(--border-default)", flexWrap: "wrap" }}
      >
        {options.map((opt) => (
          <ToggleGroupItem
            key={opt.id}
            value={opt.id}
            style={{
              background: value === opt.id ? "var(--enssa-aubergine)" : "transparent",
              color: value === opt.id ? "#fff" : "var(--fg-1)",
              padding: "5px 10px",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              fontFamily: "inherit",
              whiteSpace: "nowrap",
              height: "auto",
              cursor: "pointer",
            }}
          >
            {opt.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

export function DemographicFilterBar({ value, onChange, totalCount, filteredCount }: DemographicFilterBarProps) {
  const active = isDemographicFilterActive(value);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", padding: "8px 0" }}>
      <Cluster
        label="Sex"
        ariaLabel="Filter by sex"
        value={value.sex}
        options={sexOpts}
        onChange={(next) => onChange({ ...value, sex: next })}
      />
      <Cluster
        label="ATSI"
        ariaLabel="Filter by ATSI status"
        value={value.atsi}
        options={atsiOpts}
        onChange={(next) => onChange({ ...value, atsi: next })}
      />
      <Cluster
        label="Language"
        ariaLabel="Filter by language background"
        value={value.lbote}
        options={lboteOpts}
        onChange={(next) => onChange({ ...value, lbote: next })}
      />
      {active && totalCount != null && filteredCount != null && (
        <span style={{ fontSize: 12, color: "var(--fg-3)" }}>
          Showing <strong style={{ color: "var(--fg-1)" }}>{filteredCount}</strong> of {totalCount} students
        </span>
      )}
    </div>
  );
}
